import { PartnerAccountRequest, removeEmptyProperty } from 'vopay';
import { api } from '@/services/api.config';
import type { DetailsFormValues } from './types';
import type { ParentAccountOption } from './parentAccountOptions';

export type SubAccountSubmitResult = {
  success: boolean;
  accountID: string;
  errorMessage: string;
};

/**
 * Sub-account mode of the Add Account drawer (ID-13797, spec 5.5): one call to POST /partner/account
 * with the parent, and none of the top-level follow-ups (HubSpot, account matrix, business case, Flow
 * of Funds) — the sub-account inherits the parent's settings instead. The credentials the endpoint
 * returns are deliberately not kept. No in-flight flag: the drawer blocks the screen while this runs.
 */
export default function useSubAccountSubmit() {
  async function submitSubAccount(
    details: DetailsFormValues,
    parent: ParentAccountOption,
  ): Promise<SubAccountSubmitResult> {
    try {
      const legalBusinessName = details.legalBusinessName.trim();

      const res = await api.postPartnerAccount(
        removeEmptyProperty<PartnerAccountRequest>({
          ParentAccountID: parent.accountID,
          LegalBusinessName: legalBusinessName,
          // The endpoint requires Name; the backend stores it as the DBA only when it differs from
          // the legal name, so the fallback costs nothing.
          Name: details.dbaName.trim() || legalBusinessName,
          EmailAddress: details.emailAddress,
          // Required by the backend for a US account; the form only asks for it under a US parent,
          // so a phone typed in top-level mode and then hidden is not sent along.
          BusinessPhone: parent.country === 'US' ? details.businessPhone : '',
          // Sent per the spec; the backend derives the country from the parent regardless. The
          // welcome email is the backend's call for now (product, 2026-10-09), so no flag is sent.
          Country: parent.country,
        }),
      );

      // A success with no AccountID has nothing to navigate to; the caller shows its generic error.
      if (!res.Success || !('AccountID' in res) || !res.AccountID) {
        return { success: false, accountID: '', errorMessage: res.ErrorMessage };
      }

      return { success: true, accountID: res.AccountID, errorMessage: '' };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  return { submitSubAccount };
}
